export interface AssetsLiabilitiesColumn {
    key: string;
    title: string;
    isAmount?: boolean;
    ignoreCalculation?: boolean;
    valueLabels?: Record<string, string>;
    /** Pair with another field for display (e.g. area + unit → "1 Acre"). */
    unitKey?: string;
}

export interface AssetsLiabilitiesTable {
    key: string;
    section: string;
    parent?: string;
    title: string;
    columns: AssetsLiabilitiesColumn[];
    showSubTotal?: boolean;
    multiSubTotal?: boolean;
    /** Render as a single text line instead of a table (e.g. Annual Living Expense). */
    displayAsText?: boolean;
    /** Hide this table in the details view (still used for summary totals). */
    excludeFromView?: boolean;
}

export interface AssetsLiabilitiesSummaryItem {
    key: string;
    title: string;
}

const c = (
    key: string,
    title: string,
    isAmount = false,
    ignoreCalculation = false,
    valueLabels?: Record<string, string>,
    unitKey?: string
): AssetsLiabilitiesColumn => ({ key, title, isAmount, ignoreCalculation, valueLabels, unitKey });

const acquisitionLabels: Record<string, string> = {
    Purchased: 'Purchase',
    Inheritance: 'Inheritance',
    Gift: 'Gift',
};

const propertyTypeLabels: Record<string, string> = {
    LAND: 'Land',
    BUILDING: 'Building',
    FLAT_APARTMENT: 'Flat/Apartment',
    FLOORSPACE: 'Floorspace',
    LAND_AND_BUILDING_FLAT_FLOORSPACE: 'Land and Building/Flat/Floorspace',
};

export const ASSETS_LIABILITIES_TABLES: AssetsLiabilitiesTable[] = [
    {
        key: 'director_shareholdings_in_limited_companies', section: 'Assets', parent: 'Business Capital',
        title: 'Director Shareholdings in Limited Companies', showSubTotal: true,
        columns: [c('name', 'Name of the Company'), c('type', 'Type of Acquisition', false, false, acquisitionLabels), c('otherInfo', 'No of Shares'), c('value', 'Value', true)]
    },
    {
        key: 'business_capital_other_than_directors_shareholdings_in_limited_companies', section: 'Assets', parent: 'Business Capital',
        title: 'Business Capital Other Than Director Shareholdings in Limited Companies',
        columns: [c('name', 'Name of the Business'), c('businessAssetValue', 'Total Business Asset', true), c('businessLiabilitiesValue', 'Business Liabilities', true), c('value', 'Business Capital', true)]
    },
    {
        key: 'capital_of_partnership_firm', section: 'Assets', parent: 'Business Capital',
        title: 'Capital of Partnership Firm', showSubTotal: true,
        columns: [c('name', 'Name of Partnership Firm'), c('type', 'TIN of Firm'), c('value', 'Partners Capital', true)]
    },
    {
        key: 'non_agricultural_property_child', section: 'Assets', parent: 'Non-Agricultural Property',
        title: 'Non-Agricultural Property', showSubTotal: true, multiSubTotal: true,
        columns: [
            c('propertyType', 'Property Type', false, false, propertyTypeLabels), c('description', 'Description and Location'),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('otherInfo', 'Land Area', false, false, undefined, 'name'),
            c('floorArea', 'Floor Area', false, false, undefined, 'floorAreaUnit'),
            c('startYrAmount', 'Purchase/Acquisition cost at the start of the income year', true),
            c('endYrAmount', 'Purchase/Acquisition cost at the end of the income year', true)
        ]
    },
    {
        key: 'advance_made_for_non_agricultural_property', section: 'Assets', parent: 'Non-Agricultural Property',
        title: 'Advance Made for Non-Agricultural Property', showSubTotal: true, multiSubTotal: true,
        columns: [
            c('description', 'Particulars of the Property'),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('startYrAmount', 'Advance Amount at Start of Income Year', true),
            c('endYrAmount', 'Advance Amount at End of Income Year', true)
        ]
    },
    {
        key: 'agricultural_property', section: 'Assets', title: 'Agricultural Property', showSubTotal: true, multiSubTotal: true,
        columns: [
            c('description', 'Description and Location'),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('otherInfo', 'Area of the Property', false, false, undefined, 'name'),
            c('startYrAmount', 'Purchase/Acquisition cost at the start of the income year', true),
            c('endYrAmount', 'Purchase/Acquisition cost at the end of the income year', true)
        ]
    },
    {
        key: 'share_debenture_etc', section: 'Assets', parent: 'Financial Assets',
        title: 'Share, Debenture, Bond, Securities, Unit Certificate', showSubTotal: true,
        columns: [c('name', 'BO Account/Instrument/ISIN No.'), c('type', 'Type of Acquisition', false, false, acquisitionLabels), c('relatedIN', 'Name of Brokerage House/Bank/FI'), c('value', 'Cost Value', true)]
    },
    {
        key: 'savings_certificate_bonds_and_other_government_securities', section: 'Assets', parent: 'Financial Assets',
        title: 'Sanchayapatra', showSubTotal: true,
        columns: [c('type', 'Type of Sanchayapatra'), c('relatedIN', 'Registration No.'), c('dateOfIssue', 'Issue Date'), c('value', 'Value', true)]
    },
    {
        key: 'fix_deposits_term_deposits_and_dps', section: 'Assets', parent: 'Financial Assets',
        title: 'Fixed Deposits, Term Deposits', showSubTotal: true,
        columns: [c('description', 'Particulars'), c('name', 'Name of the Bank'), c('relatedIN', 'Account No.'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'dps', section: 'Assets', parent: 'Financial Assets', title: 'DPS', showSubTotal: true,
        columns: [c('description', 'Particulars'), c('name', 'Name of the Bank'), c('relatedIN', 'Account No.'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'loans_given_to_others', section: 'Assets', parent: 'Financial Assets', title: 'Loans Given to Others',
        columns: [c('description', 'Particulars'), c('name', 'Name of the Borrower'), c('relatedIN', 'TIN of the Borrower'), c('value', 'Amount', true)]
    },
    {
        key: 'provident_fund_and_other_fund', section: 'Assets', parent: 'Financial Assets', title: 'Provident Fund and Other Fund',
        columns: [c('name', 'Name of Employer'), c('description', 'Particulars of the Fund'), c('relatedIN', 'Account No.'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'other_financial_assets', section: 'Assets', parent: 'Financial Assets',
        title: 'Other Financial Assets', showSubTotal: true,
        columns: [c('description', 'Particulars'), c('type', 'Type of Acquisition', false, false, acquisitionLabels), c('otherInfo', 'Related Details'), c('value', 'Amount', true)]
    },
    {
        key: 'motor_car', section: 'Assets', title: 'Motor Car', showSubTotal: true,
        columns: [
            c('relatedIN', 'Registration No.'),
            c('description', 'Type and Particulars', false, false, {
                motorcycle: 'Motorcycle',
                car_micro_zeep: 'Car, Microbus, Zeep',
                any_other: 'Any other'
            }),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('otherInfo', 'Engine Capacity', false, false, undefined, 'name'),
            c('value', 'Amount', true)
        ]
    },
    {
        key: 'gold_diamond_gems_and_other_items', section: 'Assets',
        title: 'Gold, Diamond, Gems and Other Items', showSubTotal: true,
        columns: [
            c('description', 'Particulars'),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('endYrAmount', 'Quantity at the End of the Income Year', false, true, undefined, 'name'),
            c('value', 'Value', true)
        ]
    },
    {
        key: 'furniture_equipments_and_electronic_items', section: 'Assets',
        title: 'Furniture, Equipments and Electronic Items', showSubTotal: true,
        columns: [c('description', 'Particulars'), c('type', 'Type of Acquisition', false, false, acquisitionLabels), c('otherInfo', 'Quantity'), c('value', 'Value', true)]
    },
    {
        key: 'other_assets_of_significant_value', section: 'Assets',
        title: 'Other Assets of Significant Value', showSubTotal: true,
        columns: [c('description', 'Particulars'), c('type', 'Type of Acquisition', false, false, acquisitionLabels), c('otherInfo', 'Related Detail'), c('value', 'Value', true)]
    },
    {
        key: 'notes_and_currencies', section: 'Assets', parent: 'Cash and Fund Outside Business',
        title: 'Notes, Currencies, Banks, Cards and Other Electronic Cash',
        excludeFromView: true,
        columns: [c('value', 'Amount in BDT', true)]
    },
    {
        key: 'banks_cards_and_other_electronic_cash', section: 'Assets', parent: 'Cash and Fund Outside Business',
        title: 'Banks, Cards and Other Electronic Cash', showSubTotal: true,
        columns: [c('type', 'Type'), c('name', 'Bank/FI Name'), c('relatedIN', 'Account/Card no.'), c('value', 'Balance', true)]
    },
    {
        key: 'hand_cash', section: 'Assets', parent: 'Cash and Fund Outside Business',
        title: 'Cash in Hand',
        columns: [
            c('__particulars', 'Particulars'),
            c('value', 'Amount in BDT', true)
        ]
    },
    {
        key: 'other_deposits_balance_and_advance', section: 'Assets', parent: 'Cash and Fund Outside Business',
        title: 'Other Deposits, Balance and Advance',
        columns: [c('description', 'Particulars'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'outside_property', section: 'Assets', title: 'Asset Outside Bangladesh', showSubTotal: true, multiSubTotal: true,
        columns: [
            c('name', 'Description and Location'),
            c('type', 'Type of Acquisition', false, false, acquisitionLabels),
            c('startYrAmount', 'Value at Start of Income Year', true),
            c('endYrAmount', 'Value at End of Income Year', true)
        ]
    },
    {
        key: 'borrowing_from_bank_or_other_fi', section: 'Liabilities (Outside Business)',
        title: 'Borrowing from Bank or Other Financial Institution', showSubTotal: true,
        columns: [c('name', 'Name of the Bank/FI'), c('relatedIN', 'Account Number'), c('description', 'Purpose'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'unsecured_loan', section: 'Liabilities (Outside Business)', title: 'Unsecured Loan', showSubTotal: true,
        columns: [c('name', 'Name of the Lender'), c('relatedIN', 'TIN of the Lender'), c('description', 'Purpose'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'other_loan_or_overdraft', section: 'Liabilities (Outside Business)', title: 'Other Loan or Overdraft',
        columns: [c('description', 'Particulars of the Lender'), c('type', 'Type of Loan'), c('otherInfo', 'Other Detail'), c('value', 'Balance Amount', true)]
    },
    {
        key: 'annual_living_expense', section: 'Other Outflow', title: 'Annual Living Expense',
        displayAsText: true,
        columns: [c('value', 'Amount', true)]
    },
    {
        key: 'loss_deduction_other_expense', section: 'Other Outflow', title: 'Loss Deduction Other Expense',
        columns: [c('name', 'Particulars'), c('value', 'Amount', true)]
    },
    {
        key: 'gift_donation_and_contribution', section: 'Other Outflow', title: 'Gift Donation and Contribution',
        columns: [
            c('description', 'Particulars'),
            c('name', 'Name of the Receiver and Description'),
            c('relatedIN', 'TIN of the Receiver'),
            c('value', 'Amount', true)
        ]
    },
    {
        key: 'income_shown_in_the_return', section: 'Sources of Fund',
        title: 'Income Shown in the Return(excluding non cash benefits)',
        columns: [c('value', 'Amount', true)]
    },
    {
        key: 'tax_exempted_income_and_allowances', section: 'Sources of Fund',
        title: 'Tax Exempted Income and Allowances',
        columns: [c('value', 'Amount', true)]
    },
    {
        key: 'other_receipts', section: 'Sources of Fund', title: 'Other Receipts',
        columns: [c('description', 'Particulars'), c('relatedIN', 'Reference'), c('value', 'Amount', true)]
    },
    // {
    //     key: 'previous_income_year_net_wealth', section: 'Net Wealth of Previous Income Year',
    //     title: 'Net Wealth at the Last Date of Previous Income Year',
    //     columns: [c('value', 'Amount', true)]
    // },
];

export const ASSETS_SUMMARY_KEYS = [
    'director_shareholdings_in_limited_companies',
    'business_capital_other_than_directors_shareholdings_in_limited_companies',
    'capital_of_partnership_firm',
    'non_agricultural_property_child',
    'advance_made_for_non_agricultural_property',
    'agricultural_property',
    'outside_property',
    'share_debenture_etc',
    'savings_certificate_bonds_and_other_government_securities',
    'fix_deposits_term_deposits_and_dps',
    'dps',
    'loans_given_to_others',
    'other_financial_assets',
    'provident_fund_and_other_fund',
    'motor_car',
    'gold_diamond_gems_and_other_items',
    'furniture_equipments_and_electronic_items',
    'other_assets_of_significant_value',
    'notes_and_currencies',
    'hand_cash',
    'other_deposits_balance_and_advance',
];

export const LIABILITY_SUMMARY_KEYS = [
    'borrowing_from_bank_or_other_fi',
    'unsecured_loan',
    'other_loan_or_overdraft',
];

export const OTHER_OUTFLOW_SUMMARY_KEYS = [
    'annual_living_expense',
    'loss_deduction_other_expense',
    'gift_donation_and_contribution',
];

export const SOURCE_OF_FUND_SUMMARY_KEYS = [
    'income_shown_in_the_return',
    'tax_exempted_income_and_allowances',
    'other_receipts',
];

export const ASSETS_LIABILITIES_SUMMARY_ITEMS: AssetsLiabilitiesSummaryItem[] = [
    { key: 'totalGrossWealth', title: 'Gross Wealth' },
    { key: 'totalLiabilities', title: 'Total Liabilities Outside Business' },
    { key: 'totalNetWealth', title: 'Net Wealth' },
    { key: 'previousIncomeYearNetWealth', title: 'Net Wealth at the Last Date of Previous Income Year' },
    { key: 'changeInNetWealth', title: 'Change in Net Wealth' },
    { key: 'otherFundOutflow', title: 'Other Fund Outflow During Income Year' },
    { key: 'totalFundOutflow', title: 'Total Fund Outflow' },
    { key: 'sourcesOfFund', title: 'Source of Fund' },
    { key: 'difference', title: 'Difference' },
];
